// Per-browser-context MQTT clientId helpers.
//
// The broker (MQTT 3.1.1 §3.1.4-2) MUST disconnect any existing session
// whose clientId matches a new CONNECT. Our base clientId is site-scoped
// (`live-<site.name>`) on purpose: a debug page and the useLivePower hook
// mounted in the same tab both pass that base, so the shared MqttProvider
// dedupes them onto a single broker session.
//
// The failure mode that base id creates: open the same site in a *second*
// browser context — another profile, another device, an incognito window,
// or a separate automation browser — anywhere the single-active-tab Web
// Lock (navigator.locks is per-profile) can't reach. Both contexts connect
// with the identical base clientId and the broker evicts each one as the
// other reconnects, producing an endless connect -> open -> 1006 close ->
// reconnect(~5s) loop where the UI never leaves "Connecting...".
//
// The fix: the provider appends a suffix that is stable for the life of one
// JS context (one page load) but random across contexts. The broker clientId
// becomes unique per browser context, killing cross-context collisions, while
// the *base* id the callers pass stays stable — so in-tab session sharing and
// the provider's credential-equality dedup are untouched.

/** Length of the random suffix. 8 hex chars ≈ 4.3e9 space — collisions
 *  between two concurrently-open contexts are vanishingly unlikely, and the
 *  total clientId stays well within broker length limits. */
const SUFFIX_LEN = 8;

/**
 * A random suffix identifying one browser context (one page load). Uses
 * crypto.randomUUID where available, with a Math.random fallback for older
 * browsers / non-DOM test runners. Callers should generate this once and
 * reuse it for the lifetime of the context (see MqttProvider).
 */
export function makeSessionSuffix(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID().replace(/-/g, '').slice(0, SUFFIX_LEN);
    }
  } catch {
    // fall through to the Math.random path
  }
  return Math.random().toString(16).slice(2).padEnd(SUFFIX_LEN, '0').slice(0, SUFFIX_LEN);
}

/**
 * Join a stable base clientId with a per-context suffix. An empty suffix
 * returns the base unchanged (defensive — a missing suffix should degrade
 * to the old behaviour rather than produce a trailing-dash clientId).
 */
export function withSessionSuffix(baseClientId: string, suffix: string): string {
  return suffix ? `${baseClientId}-${suffix}` : baseClientId;
}
